import { Hub } from "aws-amplify";
import type { AxiosInstance } from "axios";
import { useEffect, useState } from "react";
import { AuthService } from "service/auth.service";
import { getSharedAxiosInstance } from "service/axios-instance.service";
import { CookieService } from "service/cookies";
import { STORAGE_KEYS, sessionStorageService } from "service/storage";

export interface UserAttributes {
  given_name: string;
  family_name: string;
  email: string;
  identities: string;
  company?: string;
}

/**
 * Hook for managing sign-in state and redirect state
 */
export function useSignIn(signInIdentityProvider: string): {
  isSignedIn: boolean;
  state: string | undefined;
} {
  const [isSignedIn, setIsSignedIn] = useState<boolean>(false);
  const [state, setState] = useState<string>();

  useEffect(() => {
    AuthService.hasValidSession().then(async (hasValidSession) => {
      if (hasValidSession) {
        setIsSignedIn(true);

        // Check if we have a stored state in sessionStorage
        const storedState = sessionStorageService.getItem(
          STORAGE_KEYS.AUTH_REDIRECT_STATE,
        );
        if (storedState) {
          setState(storedState);
          // Clear the stored state to prevent reusing it
          sessionStorageService.removeItem(STORAGE_KEYS.AUTH_REDIRECT_STATE);
        }
      }
    });
  }, [signInIdentityProvider]);

  useEffect(
    () =>
      Hub.listen("auth", (callback) => {
        if (callback.payload.event === "customOAuthState") {
          // If we still get a customOAuthState event, use it as a fallback
          const customState = callback.payload.data;
          if (customState && customState !== "login") {
            setState(customState);
          }
        }
      }),
    [],
  );

  return { isSignedIn, state };
}

/**
 * Hook for managing user authentication state only
 */
export function useAuthUser(): {
  user: UserAttributes | undefined;
  initializing: boolean;
} {
  const [user, setUser] = useState<UserAttributes>();
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const loadUser = () =>
      AuthService.getSignedInUser()
        .then(setUser)
        .finally(() => setInitializing(false));
    loadUser();
    return Hub.listen("auth", loadUser);
  }, []);

  return { user, initializing };
}

/**
 * Function combining authentication state with a shared axios instance
 *
 * IMPORTANT: All components calling this hook share the SAME axios instance.
 * The axios instance is NOT created per component - it's a singleton managed
 * by the axios-instance.service.
 *
 */
export function getAuthenticatedAxios(): {
  axios: AxiosInstance;
} {
  const axiosInstance = getSharedAxiosInstance({
    getAccessToken: async () => {
      const cdcToken = CookieService.getToken();
      if (cdcToken) return cdcToken;
      return AuthService.getAccessToken();
    },
  });

  return { axios: axiosInstance };
}

export enum CustomerProviderName {
  CovestroAzureSSO = "CovestroAzureSSO",
  OneLogin = "OneLogin",
}

/**
 * Initiate federated login with a custom provider
 */
export function federatedLogin(
  customProvider: CustomerProviderName,
): Promise<any> {
  const currentState =
    window.location.pathname + window.location.hash + window.location.search;
  sessionStorageService.setItem(STORAGE_KEYS.AUTH_REDIRECT_STATE, currentState);

  return AuthService.federatedSignIn(customProvider, "login");
}
